import React, {
    useEffect,
    useRef,
    useMemo,
    useState
} from 'react';
import { parseISO, format } from "date-fns";
import Accordion from '../../scripts/components/Accordion'
import { useQuery, useQueries } from '@tanstack/react-query';


import * as OPTIONS from "../option-fields";
import * as UTILS from "../utils";
import * as QUERIES from "../query-functions";

import LoadingIndicator from '../components/loading-indicator';
import AccountNavigation from './account-navigation';
import UpsellBlock from '../components/upsell-block';
import LogoutButton from '../components/logout-button';
import UpcomingTickets from './upcoming-tickets';
import ExtrasInstance from '../extras/extras-instance';
import ArrowIcon from '../svg/arrow';

const AccountOrders = () => {

    const [upcomingTickets, setUpcomingTickets] = useState(null);
    const [extrasPending, setExtrasPending] = useState(null);
    const [extras, setExtras] = useState(null);
    const [currentExtras, setCurrentExtras] = useState(null);

    const accordionRef = useRef(null);

    const { 
        isPending: ordersPending,
        data: ordersData,
    } = useQuery({
        queryKey: ['ordersData'],
        queryFn: QUERIES.getOrders,
        refetchOnWindowFocus: false,
        retry: false
    });

    const supplementaryEventData = useQueries({
        queries: upcomingTickets
        ? upcomingTickets.map((ticket) => ({
            queryKey: ['supplementaryEventData', ticket.instance.id],
            queryFn: () => QUERIES.getSupplementaryEvents(ticket.instance.id),
            enabled: true,
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            retry: false
          }))
        : []
    });


    useEffect(() => {
        if (!ordersData?.length || !accordionRef?.current) return;

        // Make sure the ordersData mapping has completed before initing the accordion
        setTimeout(() => {
            new Accordion(accordionRef.current);
        }, 1)
    }, [ordersData, accordionRef])

    useEffect(() => {
        if (!ordersData) return;

        const upcoming = UTILS.getUpcomingTickets(ordersData);
        setUpcomingTickets(upcoming);

    }, [ordersData])

    useEffect(() => {
        if (!supplementaryEventData?.length || !upcomingTickets?.length) return;

        const is_pending = supplementaryEventData.some(query => query.isLoading);
        setExtrasPending(is_pending);

        const data = supplementaryEventData.map((result, index) => {
            if (result.data) return {
                instance: upcomingTickets[index], 
                extras: result.data
            }
        }).filter((notUndefined) => notUndefined !== undefined);

        // Only set extras if the array has changed to stop infinite rerenders
        setExtras((prev) => {
            const isSame =
                prev?.length === data.length &&
                prev?.every((item, index) =>
                    item.instance === data[index].instance &&
                    item.extras === data[index].extras
                );
            return isSame ? prev : data;
        });

        if (currentExtras) return;
        setCurrentExtras(data[0]);

    }, [supplementaryEventData]);

    const handleExtrasChange = (e) => {
        if (!e) return;
        const new_extras = extras.find((extra) => extra.instance.id == e.target.value);
        setCurrentExtras(new_extras);
    }

    if (ordersPending) return <LoadingIndicator type="page" />

    return (
        <>
            <AccountNavigation />

            <div className='booking-pathway__container booking-pathway__account booking-pathway__account--orders'>

                <div className='booking-pathway__main-content'>
                    <h1 className='booking-pathway__header'>Orders & tickets</h1>

                    <UpcomingTickets upcomingTickets={upcomingTickets} />

                    {extrasPending &&
                        <LoadingIndicator />
                    }
                    {(!extrasPending && extras?.length) ?
                        <section className='orders__extras'>
                            <header className='orders__extras--header'>
                            <h2 className='booking-pathway__header'>Enhance your experience</h2>
                            {extras.length > 1 ? (
                                <select className='orders__extras--select' name="extras" id="extrasSelect" onChange={handleExtrasChange}>
                                    {extras.map((extra) => {
                                        const formatted_date = UTILS.formatDateTime(extra.instance?.instance.start);
                                        return (
                                            <option key={extra.instance?.id} value={extra.instance?.id}>
                                                {extra.instance?.event.name} {formatted_date}
                                            </option>
                                        );
                                    })}
                                </select>
                            ) : (
                                (() => {
                                    const formatted_date = UTILS.formatDateTime(currentExtras?.instance.instance.start);
                                    return <p className='orders__extras--date'>{currentExtras?.instance?.event.name} {formatted_date}</p>;
                                })()
                            )}
                            </header>

                            <div className='extras-group orders__extras--group'>
                                {currentExtras?.extras?.map(instance => (
                                    <ExtrasInstance key={instance.id} instance={instance} />
                                ))}
                            </div>
                        </section>
                    :null}

                    {ordersData?.length ?
                        <section ref={accordionRef} className="past-orders accordion">
                            <h2 className='booking-pathway__header'>Past orders</h2>

                            {ordersData.map((order) => (
                                <OrderContent key={order.id} order={order} />
                            ))}
                        </section>
                    :
                        <p>You have no previous orders.</p>
                    }
                </div>

                <div className='booking-pathway__footer'>
                    <LogoutButton />

                    <UpsellBlock content={OPTIONS.account.orders_upsell} type="orders-upsell" />
                </div>

            </div>
        </>
    );
};

const OrderContent = ({order}) => {
    
    const orderDate = useMemo(() => {
        return format(parseISO(order.lastTransactionDate), "EEE d MMM h:mma")
    }, [order.lastTransactionDate]);

    
    const groupedTickets = useMemo(() => {
        return UTILS.groupTicketsByInstance(order.tickets)
    }, [order.tickets]);

    
    const ticketsTotal = useMemo(() => {
        return UTILS.getGroupedTicketsTotal(groupedTickets);
    }, [groupedTickets]);

    
    const memershipsTotal = useMemo(() => {
        return UTILS.getMembershipsTotal(order?.membershipSubscriptions);
    }, [order]);

    
    const donationsTotal = useMemo(() => {
        return UTILS.getDonationsTotal(order?.donations);
    }, [order]);

    if (!order) return;

    return (
        <div key={order.id} className="accordion-item">

            <button className="accordion-item__header margin-trim"
                role="button"
                aria-expanded="false"
                aria-controls={order.id}>
                <h3 className="accordion-item__heading">
                    <span>{order.id}</span>
                    <span>{orderDate}</span>
                </h3>
                <span className="accordion-item__icon" aria-hidden="true">
                    <ArrowIcon />
                </span>
            </button>

            <div className="accordion-item__content-panel" id={order.id} aria-hidden="true">
                <div className="accordion-item__content-panel--inner">
                    <div className="accordion-item__content order-content">

                        {groupedTickets?.length ?
                            <section className='order-content__section order-content__section--tickets'>
                                <h4 className='order-content__header'>Tickets</h4>
                                <ul role="list" className='order-content__list'>
                                    {groupedTickets.map((ticket) => (
                                        <li key={ticket.instance_id}>
                                            <div className='order-content__list-item'>
                                                <header className='order-content__list-item--header'>
                                                    <p className='order-content__list-item--title'>{ticket.event_name}</p>
                                                    <p className='order-content__list-item--content'>{ticket.instance_date}</p>
                                                </header>
                                                <ul role="list" className='order-content__list-item--list'>
                                                    {ticket.tickets.map((instance) => (
                                                        <li key={instance.id} className='order-content__list-item--group'>
                                                            <p className='order-content__list-item--item'>
                                                                <span className='order-content__list-item--title'>Seat</span>
                                                                <span className='order-content__list-item--content'>{instance.seat?.name}</span>
                                                            </p>
                                                            <p className='order-content__list-item--item'>
                                                                <span className='order-content__list-item--title'>Type</span>
                                                                <span className='order-content__list-item--content'>{instance.ticketType?.name}</span>
                                                            </p>
                                                            <p className='order-content__list-item--item'>
                                                                <span className='order-content__list-item--title'>Total</span>
                                                                <span className='order-content__list-item--content'>£{instance.total?.toFixed(2)}</span>
                                                            </p>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <p className='order-content__list-item--total'>
                                                    <span className='order-content__list-item--title'>Tickets Total</span>
                                                    <span className='order-content__list-item--content'>£{ticketsTotal?.toFixed(2)}</span>
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                            :null
                        }

                        {order.membershipSubscriptions?.length ?
                            <section className='order-content__section order-content__section--refunds'>
                                <h4 className='order-content__header'>Memberships</h4>
                                <ul role="list" className='order-content__list'>
                                    {order.membershipSubscriptions?.map((membership) => (
                                        <li key={membership.id} className='order-content__list-item--group'>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Type</span>
                                                <span className='order-content__list-item--content'>{membership.membership?.name}</span>
                                            </p>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Total</span>
                                                <span className='order-content__list-item--content'>£{membership.total?.toFixed(2)}</span>
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                                <p className='order-content__list-item--total'>
                                    <span className='order-content__list-item--title'>Memberships Total</span>
                                    <span className='order-content__list-item--content'>£{memershipsTotal?.toFixed(2)}</span>
                                </p>
                            </section>
                            :null
                        }

                        {order.donations?.length ?
                            <section className='order-content__section order-content__section--refunds'>
                                <h4 className='order-content__header'>Donations</h4>
                                <ul role="list" className='order-content__list'>
                                    {order.donations?.map((donation) => (
                                        <li key={donation.id} className='order-content__list-item--group'>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Type</span>
                                                <span className='order-content__list-item--content'>{donation.fund?.name}</span>
                                            </p>
                                            <p className='order-content__list-item--item'>
                                                <span className='order-content__list-item--title'>Total</span>
                                                <span className='order-content__list-item--content'>£{donation.amount?.toFixed(2)}</span>
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                                <p className='order-content__list-item--total'>
                                    <span className='order-content__list-item--title'>Donation Total</span>
                                    <span className='order-content__list-item--content'>£{donationsTotal?.toFixed(2)}</span>
                                </p>
                            </section>
                            :null
                        }

                        {order.refunds?.length ?
                            <section className='order-content__section order-content__section--refunds'>
                                <h4 className='order-content__header'>Refunds</h4>
                                <ul role="list" className='order-content__list'>
                                    {order.refunds?.map((refund) => (
                                        <li key={refund.amount} className='order-content__list-item--group'>
                                            <p className='order-content__list-item--total'>
                                                <span className='order-content__list-item--title'>Refund Total</span>
                                                <span className='order-content__list-item--content'>£{refund.amount?.toFixed(2)}</span>
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                            :null
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AccountOrders;
